import React, {
  useState, useEffect, ReactElement, ChangeEvent,
} from 'react'
import styled from 'styled-components'
import { MdClear } from 'react-icons/md'
import { onlyString } from '../helpers/dataTransformer'
import colors from '../lib/colors'
import FieldStyles from '../components/FieldStyles'
import { StateAction } from '../types/typeAliases'

export type CodeFieldProps = {
  isError?: boolean
  getCode(code: string): void
  label?: string
}

const Container = styled.div`
  position: relative;
  width: fit-content;
  display: flex;
  margin-right: 10px;
`
const StyledInput = styled.input`
  &.styled-input {
    letter-spacing: 1px;
    text-align: center;
    width: 100%;
    min-width: 56px;
    max-width: 70px;
    margin-right: 10px;
  }
`

const ClearButton = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: -10px;
  display: flex;
  cursor: pointer;
  svg {
    width: 20px;
    height: auto;
  }
  transition: color 0.2s;
  color: ${colors.darkGray};
  :hover {
    color: ${colors.darkerGray};
  }
`

type CursorState = { [key: string]: number }
const NOT_ALLOWED = /[^0-9a-zA-Z]/g

function CodeInput({
  index,
  isError,
  setCursor,
  inputValue,
  setInputValue,
  setLastKey,
}: {
  index: number
  isError?: boolean
  setCursor: (state: CursorState) => void
  inputValue: string[]
  setInputValue: StateAction<string[]>
  setLastKey: StateAction<string | null>
}): ReactElement {
  const inputHandler = (
    event: ChangeEvent<HTMLInputElement>,
    currentInput: number,
  ): void => {
    const cleanInput = event.target.value.replace(NOT_ALLOWED, '').toUpperCase()
    const newInput = [...inputValue]
    newInput[currentInput] = cleanInput
    setInputValue(newInput)
    const nextInput: HTMLInputElement | null = document.querySelector(
      `input[name=code-${currentInput + 1}]`,
    )
    if (nextInput !== null && cleanInput.length === 4) {
      nextInput.focus() // if input full, focus next field
    }
  }

  const pasteHandler = (event: React.ClipboardEvent): void => {
    if (event.clipboardData) {
      const clipboard = event.clipboardData
        .getData('Text')
        .replace(NOT_ALLOWED, '')
        .toUpperCase()
        .match(/.{1,4}/g)
      if (clipboard) {
        setInputValue(clipboard)
      }
    }
  }

  return (
    <StyledInput
      type="text"
      name={`code-${index}`}
      maxLength={4}
      className={`styled-input ${isError && 'error'}`}
      onChange={(event): void => {
        setCursor({
          input: index,
          position: event.target.selectionStart
            ? event.target.selectionStart
            : 0,
        })
        inputHandler(event, index)
      }}
      onKeyPress={(event): void => setLastKey(event.key)}
      value={inputValue[index]}
      autoFocus={index === 0}
      onPaste={pasteHandler}
    />
  )
}

function CodeField(props: CodeFieldProps): ReactElement {
  const { isError, getCode, label } = props

  const [inputValue, setInputValue] = useState<string[]>(['', '', '', ''])
  const [cursor, setCursor] = useState<CursorState>({
    input: 0,
    position: 0,
  })
  const [lastKey, setLastKey] = useState<string | null>(null)

  // ensure cursor stays where the user left it
  const resetCursor = (): void => {
    let lastPosition = cursor.position
    if (lastKey && lastKey.match(NOT_ALLOWED)) {
      lastPosition -= 1
    }
    const thisInput: HTMLInputElement | null = document.querySelector(
      `input[name=code-${cursor.input}]`,
    )
    if (thisInput) {
      thisInput.selectionStart = lastPosition
      thisInput.selectionEnd = lastPosition
    }
  }

  useEffect(() => {
    resetCursor()
    if (inputValue.join('').length) {
      getCode(inputValue.join('-'))
    } else {
      getCode('')
    }
  }, [inputValue])

  const codeInputProps = {
    isError,
    setCursor,
    inputValue,
    setInputValue,
    setLastKey,
  }

  return (
    <FieldStyles label={label}>
      <Container id={label ? onlyString(label) : ''}>
        <CodeInput index={0} {...codeInputProps} />
        <CodeInput index={1} {...codeInputProps} />
        <CodeInput index={2} {...codeInputProps} />
        <CodeInput index={3} {...codeInputProps} />
        {inputValue.join('').length > 0 && (
          <ClearButton onClick={(): void => setInputValue(['', '', '', ''])}>
            <MdClear />
          </ClearButton>
        )}
      </Container>
    </FieldStyles>
  )
}

export default CodeField
